import React from "react";
import {
  Check,
  ChefHat,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2,
} from "lucide-react";
import { OrderTypeBadge } from "../../../components/OrderTypeBadge";
import { FornoItem } from "../../../services/fornoService";
import { resolveFornoBordaLabel, resolveFornoItemObservation } from "../../../utils/fornoItemUtils";
import {
  isPedidoSalaoFoodfly,
  resolveSalaoProducaoLabels,
  SALAO_CARD_SURFACE_CLASS,
} from "../../Painel/utils/origemPainel";
import SalaoProducaoOrderHeader from "../../../components/SalaoProducaoOrderHeader";

interface FornoItemCardProps {
  item: FornoItem;
  tick: number;
  isFirstColuna: boolean;
  isLastColuna: boolean;
  finalizandoId: number | null;
  canFinalizar: boolean;
  onFinalizar: (id: number) => void;
  onMover: (id: number, direcao: "esquerda" | "direita") => void;
}

interface ParsedDescription {
  sizeLabel: string | null;
  bordaLabel: string | null;
  sabores: string[];
}

const formatTempo = (totalSegundos: number): string => {
  const min = Math.floor(totalSegundos / 60);
  const seg = totalSegundos % 60;
  return `${String(min).padStart(2, "0")}:${String(seg).padStart(2, "0")}`;
};

const calcularSegundosDesde = (isoDate: string): number => {
  const inicioMs = new Date(isoDate).getTime();
  if (Number.isNaN(inicioMs)) return 0;
  return Math.max(0, Math.floor((Date.now() - inicioMs) / 1000));
};

const parseDescription = (descricao: string): ParsedDescription => {
  const trimmed = descricao.trim();
  if (!trimmed) {
    return { sizeLabel: null, bordaLabel: null, sabores: [] };
  }

  if (trimmed.includes("\n")) {
    const linhas = trimmed
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    const sizeLabel = linhas[0] || null;
    const bordaLabel = linhas[1] || null;
    const saboresStr = linhas[2] || "";
    const sabores = saboresStr
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s && s !== "—");
    return { sizeLabel, bordaLabel, sabores };
  }

  const parts = trimmed
    .split(" - ")
    .map((p) => p.trim())
    .filter(Boolean);
  const sizeLabel = parts[0] || null;
  if (parts.length === 1) {
    return { sizeLabel, bordaLabel: null, sabores: [] };
  }
  const bordaLabel = parts.length >= 3 ? parts[parts.length - 1] : null;
  const saboresStr =
    parts.length >= 2
      ? parts.slice(1, parts.length - (bordaLabel ? 1 : 0)).join(" - ")
      : "";
  const sabores = saboresStr
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return { sizeLabel, bordaLabel, sabores };
};

const removeSaborFractionPrefix = (value: string): string =>
  value.replace(/^\s*\d+\s*\/\s*\d+\s*[-–—:]?\s*/i, "").trim();

const FornoItemCard: React.FC<FornoItemCardProps> = ({
  item,
  tick,
  isFirstColuna,
  isLastColuna,
  finalizandoId,
  canFinalizar,
  onFinalizar,
  onMover,
}) => {
  void tick;

  const inicioRef = item.startedAt || item.createdAt;
  const tempoDisplay = item.finishedAt
    ? Math.floor(
        (new Date(item.finishedAt).getTime() - new Date(inicioRef).getTime()) /
          1000
      )
    : calcularSegundosDesde(inicioRef);
  const { sizeLabel, sabores } = parseDescription(item.descricao);
  const bordaLabel = resolveFornoBordaLabel(item.descricao, item.borda);
  const totalSabores = sabores.length;
  const saboresFormatados = sabores.map((sabor) => {
    const saborSemFracao = removeSaborFractionPrefix(sabor);
    const saborBase = saborSemFracao || sabor;
    return totalSabores > 0 ? `1/${totalSabores} - ${saborBase}` : saborBase;
  });
  const pedidoObservation = resolveFornoItemObservation(item);
  const observationPulseClass =
    "border-error-dark bg-error-light text-error-text shadow-[0_0_0_0_rgb(220_38_38/55%)] animate-forno-observation-pulse";

  const iconSize = 14;
  const isSalao = isPedidoSalaoFoodfly({ origem: item.origem });
  const lineClass = isSalao
    ? "mt-0.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-white/90"
    : "mt-0.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-neutral-600";
  const customerWeb = !isSalao
    ? item.pedidoIntegracao?.customerName ?? null
    : null;
  const salaoLabels = isSalao
    ? resolveSalaoProducaoLabels({
        mesaId: item.salaoMesaId,
        comandaId: item.salaoComandaId,
        identificador: item.salaoIdentificador,
        displayId: item.displayId,
      })
    : null;

  return (
    <div
      data-salao-card={isSalao ? "" : undefined}
      className={`rounded-lg border p-4 uppercase shadow-sm transition-shadow hover:shadow-md ${
        isSalao
          ? SALAO_CARD_SURFACE_CLASS
          : "border-neutral-200 bg-white"
      }`}
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="flex flex-col gap-1.5">
          <SalaoProducaoOrderHeader
            labels={salaoLabels}
            fallbackBadge={
              isSalao ? undefined : `Pedido #${item.displayId}`
            }
            customerName={customerWeb}
            showSalaoTag={isSalao}
          />
          {item.responsavelNome && (
            <p className={lineClass}>
              <ChefHat size={iconSize} />
              {item.responsavelNome}
            </p>
          )}
        </div>
        <span
          className={`flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums ${
            item.finishedAt
              ? "bg-emerald-100 text-emerald-700"
              : "bg-orange-100 text-orange-700"
          }`}
        >
          <Clock size={11} />
          {formatTempo(tempoDisplay)}
        </span>
      </div>

      <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
        <div className="flex flex-col gap-2.5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                Tamanho
              </span>
              <span className="mt-1 inline-flex max-w-full break-words rounded-md border border-amber-200 bg-amber-100 px-2.5 py-1 text-sm font-bold leading-snug text-amber-800">
                {sizeLabel || "Nao identificado"}
              </span>
            </div>
            {canFinalizar && (
              <button
                onClick={() => onFinalizar(item.id)}
                disabled={finalizandoId === item.id}
                className="flex shrink-0 items-center gap-1.5 self-start rounded-md border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white shadow-sm transition-colors hover:border-emerald-700 hover:bg-emerald-700 active:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {finalizandoId === item.id ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Check size={14} strokeWidth={2.5} />
                )}
                Finalizar
              </button>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
              Borda
            </span>
            <p className="break-words text-sm font-bold leading-snug text-slate-700">
              {bordaLabel || "Sem borda"}
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
              Sabores
            </span>
            {saboresFormatados.length > 0 ? (
              <div className="space-y-1">
                {saboresFormatados.map((sabor, index) => (
                  <p
                    key={`${item.id}-sabor-${index}`}
                    className="break-words text-sm font-bold leading-snug text-slate-700"
                  >
                    {sabor}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-sm font-bold leading-snug text-slate-500">
                Sem sabores
              </p>
            )}
          </div>

          {pedidoObservation ? (
            <>
              <div className="my-1 border-t border-slate-300" />
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                  Observação
                </span>
                <p
                  className={`break-words rounded-md border px-2.5 py-1.5 text-xs font-bold leading-snug shadow-sm transition-colors duration-300 ${observationPulseClass}`}
                >
                  {pedidoObservation}
                </p>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <button
            onClick={() => onMover(item.id, "esquerda")}
            disabled={isFirstColuna}
            className={`flex items-center justify-center rounded-md border px-1.5 py-1 text-[11px] transition-colors active:bg-neutral-200 disabled:cursor-not-allowed disabled:hover:bg-transparent ${
              isSalao
                ? "border-white/40 text-white hover:bg-white/10 disabled:border-white/20 disabled:text-white/35"
                : "border-neutral-300 text-neutral-600 hover:bg-neutral-100 disabled:border-neutral-200 disabled:text-neutral-300"
            }`}
          >
            <ChevronLeft size={16} strokeWidth={2.5} />
          </button>

          <button
            onClick={() => onMover(item.id, "direita")}
            disabled={isLastColuna}
            className={`flex items-center justify-center rounded-md border px-1.5 py-1 text-[11px] transition-colors active:bg-neutral-200 disabled:cursor-not-allowed disabled:hover:bg-transparent ${
              isSalao
                ? "border-white/40 text-white hover:bg-white/10 disabled:border-white/20 disabled:text-white/35"
                : "border-neutral-300 text-neutral-600 hover:bg-neutral-100 disabled:border-neutral-200 disabled:text-neutral-300"
            }`}
          >
            <ChevronRight size={16} strokeWidth={2.5} />
          </button>
        </div>

        {!isSalao && (
          <OrderTypeBadge
            orderType={item.pedidoIntegracao?.orderType}
            iconSize={18}
          />
        )}
      </div>
    </div>
  );
};

export default FornoItemCard;
